// Currency-aware money formatter bound to the active display currency.
// When a single market is selected the values are shown in that market's local
// currency; otherwise in the chosen reporting currency (default EUR).
//
// The conversion rate comes from `fx_rates` (ECB, refreshed daily) — never from
// a constant in the frontend, which is how the dashboard once reported USD ~7%
// high after the table was corrected.
import { useFilters } from "@/lib/filters";
import { SYMBOL, MARKET_CURRENCY, type FxMap } from "@/lib/currency";
import { useReference } from "@/lib/use-reference";
import { compact } from "@/lib/format";
import type { CurrencyCode } from "@/lib/api/types";

export interface Money {
  code: CurrencyCode;
  symbol: string;
  /** Format an EUR-canonical value in the active currency. */
  fmt: (eur: number | null | undefined, opts?: { compact?: boolean }) => string;
}

export function money(code: CurrencyCode, rates: FxMap): Money {
  const symbol = SYMBOL[code];
  const rate = rates[code];
  return {
    code,
    symbol,
    fmt: (eur, opts) => {
      // No rate yet (reference still loading) means the amount genuinely is not
      // known in this currency — better an em-dash for an instant than a number
      // converted at a guessed rate.
      if (eur == null || Number.isNaN(eur) || !rate) return "—";
      const v = eur / rate;
      if (opts?.compact) return `${symbol}${compact(v)}`;
      // Round amounts below €10 to cents: a cost-per-application of €0.39 became
      // "€0", which reads as missing data rather than a very low cost.
      const abs = Math.abs(v);
      return abs > 0 && abs < 10
        ? `${symbol}${v.toFixed(2)}`
        : `${symbol}${Math.round(v).toLocaleString("en-US")}`;
    },
  };
}

export function useMoney(): Money {
  const f = useFilters();
  const { fx } = useReference();
  const code: CurrencyCode = f.region !== "all" ? MARKET_CURRENCY[f.region] : f.currency;
  return money(code, fx);
}

/** Metrics are already converted per day by the reporting-currency RPC.
 * Budgets and benchmark targets still use money()/useMoney() (stored EUR). */
export function metricMoney(code: CurrencyCode): Money {
  return money(code, { [code]: 1 });
}

export function useMetricMoney(): Money {
  const f = useFilters();
  return metricMoney(f.region === "all" ? f.currency : MARKET_CURRENCY[f.region]);
}
